// Typed wrappers for the Phase 3 endpoints + the direct-to-Cloudinary
// upload step. Keeps the components simple and the Network tab readable.
//
// All requests use `credentials: 'include'` so the somesay_session
// HttpOnly cookie set by /api/auth/verify reaches the function — every
// player-facing endpoint derives player_id from the session.

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) {
    let detail;
    try {
      detail = await res.json();
    } catch {
      detail = { message: await res.text() };
    }
    const err = new Error(detail.message || detail.error || `${url} → ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

async function getJson(url) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let detail;
    try {
      detail = await res.json();
    } catch {
      detail = { message: await res.text() };
    }
    const err = new Error(detail.message || detail.error || `${url} → ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

// ---- Photo upload + verification ----------------------------------

export function newSubmissionId() {
  return `sub_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export async function signUpload({ tripId, challengeId, submissionId }) {
  return postJson('/api/photo/sign-upload', {
    trip_id: tripId,
    challenge_id: challengeId,
    submission_id: submissionId,
  });
}

// Upload a File directly to Cloudinary using server-signed params.
// Returns the secure URL of the uploaded asset.
export async function uploadToCloudinary({ file, signed, onProgress }) {
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', signed.api_key);
  form.append('timestamp', String(signed.timestamp));
  form.append('signature', signed.signature);
  form.append('folder', signed.folder);
  form.append('tags', signed.tags);
  // Phase 9: image_metadata is signed server-side and must be sent in
  // the form so Cloudinary returns the EXIF blob in the response.
  if (signed.image_metadata) {
    form.append('image_metadata', String(signed.image_metadata));
  }
  // Phase 9 (spec §23): authenticated upload type so delivery requires
  // signing. Must echo back to Cloudinary because it's part of the
  // signed param set.
  if (signed.type) {
    form.append('type', String(signed.type));
  }

  // Use XMLHttpRequest so we can stream upload progress to the UI.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', signed.upload_url, true);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (err) {
          reject(new Error('Cloudinary returned non-JSON'));
        }
      } else {
        reject(new Error(`Cloudinary upload failed: ${xhr.status} ${xhr.responseText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Cloudinary upload network error'));
    xhr.send(form);
  });
}

export async function verifyPhoto({ tripId, challengeId, submissionId, photoUrl, location, timestamp, testMode, exif }) {
  return postJson('/api/photo/verify', {
    trip_id: tripId,
    challenge_id: challengeId,
    submission_id: submissionId,
    photo_url: photoUrl,
    claimed_location: location,
    timestamp,
    test_mode: testMode,
    exif: exif || null,
  });
}

export async function verifyMultiPhoto({ tripId, challengeId, submissionId, photoUrls, location, timestamp, testMode, exifList }) {
  return postJson('/api/photo/multi-verify', {
    trip_id: tripId,
    challenge_id: challengeId,
    submission_id: submissionId,
    photo_urls: photoUrls,
    claimed_location: location,
    timestamp,
    test_mode: testMode,
    exif_list: Array.isArray(exifList) ? exifList : null,
  });
}

export async function verifyText({ tripId, challengeId, submissionId, text, testMode }) {
  return postJson('/api/text/verify', {
    trip_id: tripId,
    challenge_id: challengeId,
    submission_id: submissionId,
    text,
    test_mode: testMode,
  });
}

// ---- Transmissions + leaderboard ---------------------------------

export async function fireChallenge({ tripId, challengeId, audience, testMode, timezone }) {
  return postJson('/api/challenge/fire', {
    trip_id: tripId,
    challenge_id: challengeId,
    audience,
    test_mode: testMode,
    timezone,
  });
}

export async function dismissTransmission({ tripId, transmissionId, challengeId, testMode }) {
  return postJson('/api/wilman/dismiss', {
    trip_id: tripId,
    transmission_id: transmissionId,
    challenge_id: challengeId,
    test_mode: testMode,
  });
}

export async function getTransmissions({ tripId, dayKey, testMode }) {
  const params = new URLSearchParams({ trip_id: tripId });
  if (dayKey) params.set('day_key', dayKey);
  if (testMode) params.set('test_mode', 'true');
  return getJson(`/api/challenge/fire?${params}`);
}

// Trip-wide backlog for the redesigned Wilman tab. Returns
// open / completed / missed buckets across every challenge fired this
// trip that this player can see. Server applies audience + dismiss
// filters; client just renders.
export async function getBacklog({ tripId, testMode } = {}) {
  const params = new URLSearchParams({ trip_id: tripId });
  if (testMode) params.set('test_mode', 'true');
  return getJson(`/api/wilman/backlog?${params}`);
}

export async function getLeaderboard({ tripId, testMode }) {
  const params = new URLSearchParams({ trip_id: tripId });
  if (testMode) params.set('test_mode', 'true');
  return getJson(`/api/leaderboard?${params}`);
}
